import React, { useCallback, useEffect, useState } from 'react';

import IconButton from '@atlaskit/button/icon/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Text } from '@atlaskit/primitives/compiled/text';
import { teamsClient } from '@atlaskit/teams-client/main';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRelayEnvironment } from 'react-relay';

import { useRovoWorkspace } from '@townsquare/collab-context';
import { useFlagActions } from '@townsquare/flags';
import { useOrgContextOrgId } from '@townsquare/org-context';
import { useRouter } from '@townsquare/router/useRouter';

import { useTeamsSettingsContext } from '../../context/TeamsSettingsContext';
import { useTeamSettingsPath } from '../../hooks/useTeamSettingsPath';

import DeleteTeamTypeModal from './DeleteTeamTypeModal';
import { deleteTeamTypeMutation } from './mutations/DeleteTeamTypeMutation';

export const EditTeamTypeActionButton = (): React.JSX.Element => {
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const { defaultTeamType, editingTeamTypeName, editingTeamTypeId, editingTeamTypeIsDefault } =
		useTeamsSettingsContext();
	const environment = useRelayEnvironment();
	const [, routerActions] = useRouter();
	const teamTypesPath = useTeamSettingsPath('TEAM_TYPES');
	const { addFlag } = useFlagActions();
	const { formatMessage } = useIntl();
	const [connectedTeamsCount, setConnectedTeamsCount] = useState<number | undefined>();
	const orgId = useOrgContextOrgId();
	const siteId = useRovoWorkspace();

	useEffect(() => {
		// TODO: Replace with AGG when it's published there
		if (editingTeamTypeId) {
			teamsClient.setContext({
				cloudId: siteId,
				orgId,
			});
			teamsClient
				.getAllTeams({
					orgId,
					teamTypeIdsFilter: [editingTeamTypeId],
					limit: 51,
					memberAccountIds: [],
				})
				.then(({ teams }) => {
					setConnectedTeamsCount(teams.length);
				});
		}
	}, [editingTeamTypeId, orgId, siteId]);

	const handleDelete = useCallback(() => {
		if (!editingTeamTypeId) {
			return;
		}

		deleteTeamTypeMutation(
			environment,
			{ id: editingTeamTypeId },
			{
				onCompleted: (_, errors) => {
					setIsDeleteModalOpen(false);

					if (errors && errors.length > 0) {
						addFlag({
							id: 'team-type-delete-error',
							type: 'error',
							shouldAutoDismiss: true,
							title: formatMessage({
								id: 'townsquare.teams-settings.delete-team-type.error-flag-title',
								defaultMessage: 'Something went wrong',
								description: 'Error flag title when team type delete fails',
							}),
							description: formatMessage({
								id: 'townsquare.teams-settings.delete-team-type.error-flag-description',
								defaultMessage: 'Try again later',
								description: 'Error flag description when team type delete fails',
							}),
						});
						return;
					}

					addFlag({
						id: 'team-type-delete-success',
						type: 'success',
						shouldAutoDismiss: true,
						title: formatMessage({
							id: 'townsquare.teams-settings.delete-team-type.success-flag-title',
							defaultMessage: "We're deleting your team type",
							description: 'Success flag title when team type delete succeeds',
						}),
						description: formatMessage(
							{
								id: 'townsquare.teams-settings.delete-team-type.success-flag-description',
								defaultMessage:
									"{teamsCount, plural, one {# team is being moved to the {defaultTeamType} team type. You'll find this team under their new team type once the update is complete.} other {# teams are being moved to the {defaultTeamType} team type. You'll find these teams under their new team type once the update is complete.}}",
								description: 'Success flag description when team type delete succeeds',
							},
							{
								teamsCount: connectedTeamsCount || 0,
								defaultTeamType,
							},
						),
					});

					routerActions.push(teamTypesPath);
				},
				onError: () => {
					setIsDeleteModalOpen(false);

					addFlag({
						id: 'team-type-delete-error',
						type: 'error',
						shouldAutoDismiss: true,
						title: formatMessage({
							id: 'townsquare.teams-settings.edit-team-type-action-button.error-flag-title',
							defaultMessage: 'Something went wrong',
							description: 'Error flag title when team type delete fails',
						}),
						description: formatMessage({
							id: 'townsquare.teams-settings.edit-team-type-action-button.error-flag-description',
							defaultMessage: 'Try again later',
							description: 'Error flag description when team type delete fails',
						}),
					});
				},
			},
		);
	}, [
		editingTeamTypeId,
		environment,
		addFlag,
		formatMessage,
		connectedTeamsCount,
		defaultTeamType,
		routerActions,
		teamTypesPath,
	]);

	return (
		<>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...props }) => (
					<IconButton {...props} icon={ShowMoreIcon} label="more" ref={triggerRef} />
				)}
				shouldRenderToParent
			>
				<DropdownItemGroup>
					<DropdownItem
						onClick={() => setIsDeleteModalOpen(true)}
						isDisabled={editingTeamTypeIsDefault}
					>
						<Text color={editingTeamTypeIsDefault ? 'color.text.disabled' : 'color.text.danger'}>
							<FormattedMessage
								id="townsquare.teams-settings.header.delete-team-type-action-button"
								defaultMessage="Delete type"
							/>
						</Text>
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>

			{isDeleteModalOpen && (
				<DeleteTeamTypeModal
					onClose={() => setIsDeleteModalOpen(false)}
					onDelete={handleDelete}
					teamTypeName={editingTeamTypeName || ''}
					teamsCount={connectedTeamsCount || 0}
					defaultTeamTypeName={defaultTeamType}
				/>
			)}
		</>
	);
};
